import React from 'react';
import PropTypes from 'prop-types';

import {Alert, Box, ButtonBase, Card, CircularProgress, Grid2 as Grid, Menu, MenuItem, Stack, Typography} from 'views/components';
import {KeyboardArrowDownRounded, KeyboardArrowLeftRounded, KeyboardArrowRightRounded} from 'views/icons';

import {Translate, translate} from '../Translate';

const CompactCardTable = (props) => {
  const {
    data,
    card: CardRender,
    cardProps,
    getItemId,
    hasMore,
    isLoading,
    loadMore,
    cardsPerRow = 3,
    initialRowsPerPage = 90,
    rowsPerPageOptions = [60, 90, 150, 300],
  } = props;

  const safeCardsPerRow = Math.max(1, Math.floor(Number(cardsPerRow) || 1));
  const [page, setPage] = React.useState(1);
  const [rowsPerPage, setRowsPerPage] = React.useState(initialRowsPerPage);
  const [rowsPerPageAnchorEl, setRowsPerPageAnchorEl] = React.useState(null);

  const pageNumberLimit = 10;

  React.useEffect(() => {
    const endRecord = page * rowsPerPage;

    if (endRecord >= data.length && hasMore && !isLoading && loadMore) {
      loadMore();
    }
  }, [page, rowsPerPage, data.length, hasMore, isLoading, loadMore]);

  const totalPages = React.useMemo(() => {
    return Math.max(1, Math.ceil(data.length / rowsPerPage));
  }, [data.length, rowsPerPage]);

  React.useEffect(() => {
    if (hasMore || page <= totalPages) {
      return;
    }

    setPage(totalPages);
  }, [hasMore, page, totalPages]);

  const {startIndex, endIndex} = React.useMemo(() => {
    const nextStartIndex = (page - 1) * rowsPerPage;
    const nextEndIndex = Math.min(nextStartIndex + rowsPerPage, data.length);

    return {
      startIndex: nextStartIndex,
      endIndex: nextEndIndex,
    };
  }, [page, rowsPerPage, data.length]);

  const paginatedData = React.useMemo(() => {
    return data.slice(startIndex, endIndex);
  }, [data, startIndex, endIndex]);

  const totalCountLabel = React.useMemo(() => {
    return translate(
        hasMore ?
          'table.LabelDisplayedRowsMore' :
          'table.LabelDisplayedRowsNoMore',
        {from: startIndex + 1, to: endIndex, numOfRows: data.length},
    );
  }, [data.length, endIndex, hasMore, startIndex]);

  const currentPageGroupStart = React.useMemo(() => {
    return Math.floor((page - 1) / pageNumberLimit) * pageNumberLimit + 1;
  }, [page, pageNumberLimit]);

  const pageNumbers = React.useMemo(() => {
    const lastPageInGroup = Math.min(currentPageGroupStart + pageNumberLimit - 1, totalPages);

    return Array.from({length: lastPageInGroup - currentPageGroupStart + 1}, (_, index) => {
      return currentPageGroupStart + index;
    });
  }, [currentPageGroupStart, pageNumberLimit, totalPages]);

  const canGoToPreviousPage = page > 1;
  const canGoToNextPage = page < totalPages;
  const hasNextPage = page < totalPages || hasMore;

  const handleRowsPerPageChange = (value) => {
    setRowsPerPage(parseInt(value, 10));
    setPage(1);
    setRowsPerPageAnchorEl(null);
  };

  return (
    <Stack spacing={4} sx={{width: '100%'}}>
      <Grid
        container
        columns={{xs: 12, md: safeCardsPerRow}}
        columnSpacing={2}
        rowSpacing={2}
        alignItems='stretch'
      >
        {
          paginatedData.map((item, index) => {
            const itemId = getItemId ? getItemId(item) : startIndex + index;

            return (
              <Grid
                key={itemId}
                size={{
                  xs: 12,
                  md: 1,
                }}
                sx={{display: 'flex'}}
              >
                <Card
                  sx={{
                    'width': '100%',
                    'border': '1px solid #E5E5E5',
                    'borderRadius': '8px',
                    'boxShadow': 'none',
                    'overflow': 'hidden',
                    '& > *': {
                      height: '100%',
                    },
                  }}
                >
                  <CardRender item={item} {...cardProps} />
                </Card>
              </Grid>
            );
          })
        }
      </Grid>
      <Stack>
        {
          isLoading &&
          <Box
            sx={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gridColumn: '1 / -1',
            }}
          >
            <CircularProgress />
          </Box>
        }
        {
          !isLoading && !data.length &&
          <Box
            sx={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Alert severity="info" sx={{mt: 2}}>
              <Translate expression='table.NoResult'/>
            </Alert>
          </Box>
        }
        {
          Boolean(data.length) &&
          <Stack
            direction={{xs: 'column', md: 'row'}}
            alignItems={{xs: 'flex-start', md: 'center'}}
            justifyContent="space-between"
            spacing={1.5}
            sx={{width: '100%'}}
          >
            <Stack direction='row' alignItems='center' spacing={0.25}>
              <Typography
                sx={{
                  fontSize: '10px',
                  fontWeight: 400,
                  lineHeight: '13px',
                  color: '#818181',
                }}
              >
                {`${translate('table.RowsPerPage')}:`}
              </Typography>
              <React.Fragment>
                <ButtonBase
                  onClick={(event) => setRowsPerPageAnchorEl(event.currentTarget)}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '2px',
                    px: '7px',
                    py: '5px',
                    minWidth: '38px',
                    height: '23px',
                    borderRadius: '4px',
                    border: '1px solid #EBEBEB',
                    backgroundColor: '#FFFFFF',
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: '10px',
                      fontWeight: 400,
                      lineHeight: '13px',
                      color: '#000000',
                    }}
                  >
                    {rowsPerPage}
                  </Typography>
                  <KeyboardArrowDownRounded
                    sx={{
                      fontSize: '13px',
                      color: '#6B6760',
                    }}
                  />
                </ButtonBase>
                <Menu
                  anchorEl={rowsPerPageAnchorEl}
                  open={Boolean(rowsPerPageAnchorEl)}
                  onClose={() => setRowsPerPageAnchorEl(null)}
                  MenuListProps={{
                    sx: {
                      py: 0.5,
                    },
                  }}
                  slotProps={{
                    paper: {
                      sx: {
                        borderRadius: '8px',
                        boxShadow: '0px 4px 12px 0px #00000014',
                        mt: 0.5,
                      },
                    },
                  }}
                >
                  {
                    rowsPerPageOptions.map((option) => {
                      return (
                        <MenuItem
                          key={option}
                          onClick={() => handleRowsPerPageChange(option)}
                          sx={{
                            fontSize: '11px',
                            minHeight: '32px',
                          }}
                        >
                          {option}
                        </MenuItem>
                      );
                    })
                  }
                </Menu>
              </React.Fragment>
            </Stack>

            <Stack
              direction='row'
              alignItems='center'
              spacing={0.625}
              sx={{
                mx: {md: 'auto'},
                flexWrap: 'wrap',
              }}
            >
              <ButtonBase
                disableRipple={!canGoToPreviousPage}
                onClick={() => {
                  if (!canGoToPreviousPage) {
                    return;
                  }

                  setPage((currentPage) => currentPage - 1);
                }}
                sx={{
                  width: '23px',
                  height: '23px',
                  borderRadius: '7px',
                  color: canGoToPreviousPage ? '#6B6760' : '#A8A49E',
                }}
              >
                <KeyboardArrowLeftRounded sx={{fontSize: '14px'}} />
              </ButtonBase>
              {
                pageNumbers.map((pageNumber) => {
                  const isSelected = pageNumber === page;

                  return (
                    <ButtonBase
                      key={pageNumber}
                      onClick={() => setPage(pageNumber)}
                      sx={{
                        width: '23px',
                        height: '23px',
                        borderRadius: '7px',
                        backgroundColor: isSelected ? '#DA7955' : 'transparent',
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: '10px',
                          fontWeight: isSelected ? 600 : 400,
                          lineHeight: '13px',
                          color: isSelected ? '#FFFFFF' : '#818181',
                        }}
                      >
                        {pageNumber}
                      </Typography>
                    </ButtonBase>
                  );
                })
              }
              {
                hasNextPage &&
                <ButtonBase
                  disableRipple={!canGoToNextPage}
                  onClick={() => {
                    if (!canGoToNextPage) {
                      return;
                    }

                    setPage((currentPage) => currentPage + 1);
                  }}
                  sx={{
                    width: '23px',
                    height: '23px',
                    borderRadius: '7px',
                    border: canGoToNextPage ? '1px solid #E8E4DE' : '1px solid transparent',
                    color: canGoToNextPage ? '#6B6760' : '#C7C3BD',
                  }}
                >
                  <KeyboardArrowRightRounded sx={{fontSize: '14px'}} />
                </ButtonBase>
              }
            </Stack>

            <Typography
              sx={{
                fontSize: '10px',
                fontWeight: 400,
                lineHeight: '13px',
                color: '#818181',
                whiteSpace: 'nowrap',
              }}
            >
              {totalCountLabel}
            </Typography>
          </Stack>
        }
      </Stack>
    </Stack>
  );
};

CompactCardTable.propTypes = {
  data: PropTypes.array.isRequired,
  card: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.object,
  ]).isRequired,
  cardProps: PropTypes.object,
  cardsPerRow: PropTypes.number,
  getItemId: PropTypes.func,
  hasMore: PropTypes.bool,
  initialRowsPerPage: PropTypes.number,
  isLoading: PropTypes.bool,
  loadMore: PropTypes.func,
  rowsPerPageOptions: PropTypes.arrayOf(PropTypes.number),
};

export default React.memo(CompactCardTable);
